import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { OutreachSourceType } from "common/types";

import { INTEGRATIONS_MAP } from "../constants";
import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import {
  IntegrationApiKeySchema,
  type IntegrationApiKeySchemaType,
} from "../schema";
import EmailBisonCustomUrlField from "./EmailBisonCustomUrlField";

interface ConnectOutreachIntegrationProps {
  type: OutreachSourceType;
}

export default function ConnectOutreachIntegration({
  type,
}: ConnectOutreachIntegrationProps) {
  const isDesktop = useTwBreakpoint("lg");
  const isEmailBison = type === "emailbison";

  const { connectOutreachIntegration, isConnectingOutreachIntegration } =
    useIntegrationMutations();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: valibotResolver(IntegrationApiKeySchema),
    defaultValues: { api_key: "", custom_url: "" },
  });

  function onSubmit({ api_key, custom_url }: IntegrationApiKeySchemaType) {
    connectOutreachIntegration({
      type,
      apiKey: api_key,
      customUrl: custom_url || null,
    });
  }

  const { title } = INTEGRATIONS_MAP[type];

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component onAfterClose={reset}>
      <Trigger asChild>
        <Button variant="secondary-black" size="sm">
          Connect
        </Button>
      </Trigger>
      <Content>
        <Header title={<>Paste your {title} API key</>} />

        <Body className="gap-y-4">
          <FormField
            label="API key"
            htmlFor={`${type}-api-key`}
            hint="Keep your API key safe and do not share it with third parties"
            error={errors.api_key?.message}
          >
            <Input
              id={`${type}-api-key`}
              placeholder="Enter your API key here"
              {...register("api_key")}
            />
          </FormField>

          <RenderIf condition={isEmailBison}>
            <EmailBisonCustomUrlField
              error={errors.custom_url?.message}
              registration={register("custom_url")}
            />
          </RenderIf>
        </Body>

        <Footer className="justify-end">
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isConnectingOutreachIntegration}
          >
            Connect
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
